import React, { useState } from 'react'
import styled from 'styled-components'
import {
  Card,
  Divider,
  Flex,
  Heading1,
  IconButton,
  LinkButton,
  Text,
  TextField
} from 'adl'
import { Row, Col } from 'react-grid-system'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, Redirect } from 'react-router-dom'
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemPanel
} from 'react-accessible-accordion'

import numeral from 'numeral'
import moment from 'moment'

import LabelValue from 'components/LabelValue'
import ListItem from 'components/ListItem'
import StandardLayout from 'components/layouts/Standard'

const TransactionAccordion = styled(Accordion)`
  .accordion__button,
  .accordion__item {
    &:focus {
      outline: none;
    }
  }
`

const ChevronRight = props => <i className="icon-chevron-down" {...props} />

const NotesManager = ({ note, onNoteChange }) => {
  const [editingNote, setEditingNote] = useState(false)
  const [noteText, setNoteText] = useState(note || '')

  return (
    <div>
      <strong>Note:</strong>
      {!editingNote && (
        <>
          {'  '}
          {note || 'No note.'}{' '}
          <IconButton
            onClick={() => {
              setEditingNote(true)
              setNoteText(note || '')
            }}
          >
            <i className="icon-pencil" />
          </IconButton>
        </>
      )}
      {editingNote && (
        <>
          <TextField
            value={noteText || ''}
            onChange={evt => setNoteText(evt.target.value)}
            style={{ marginBottom: '0.25rem' }}
          />

          <div style={{ display: 'flex' }}>
            <LinkButton
              onClick={() => {
                setEditingNote(false)
                onNoteChange(noteText)
              }}
            >
              Done
            </LinkButton>
            <LinkButton color="gray600" onClick={() => setEditingNote(false)}>
              Cancel
            </LinkButton>
          </div>
        </>
      )}
    </div>
  )
}

const TransactionListItem = ({
  label,
  logo,
  icon,
  amount,
  isCredit,
  date,
  note,
  onNoteChange,
  ...rest
}) => {
  return (
    <AccordionItem>
      <AccordionItemButton>
        <ListItem {...rest}>
          <Flex justify="space-between">
            <div>
              {logo && (
                <div
                  style={{
                    width: 64,
                    height: 32,
                    textAlign: 'center',
                    marginRight: 24
                  }}
                >
                  <img
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                    src={logo}
                  />
                </div>
              )}
              {!logo && icon && (
                <div
                  style={{
                    width: 64,
                    height: 32,
                    textAlign: 'center',
                    marginRight: 24
                  }}
                >
                  <img
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                    src={icon}
                  />
                </div>
              )}
            </div>
            <div style={{ flexGrow: 1 }}>{label}</div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
            >
              <div style={{ textAlign: 'right' }}>
                <ListItem.SubText>
                  {moment(date).format('MM/DD/YY')}
                </ListItem.SubText>
                <ListItem.LargeText color={isCredit ? 'success' : null}>
                  {isCredit && '+'}
                  {numeral(amount).format('$0,0.00')}
                </ListItem.LargeText>
              </div>
              <ChevronRight style={{ marginLeft: '0.5rem' }} />
            </div>
          </Flex>
        </ListItem>
      </AccordionItemButton>
      <AccordionItemPanel>
        <div style={{ padding: '1rem' }}>
          <NotesManager note={note} onNoteChange={onNoteChange} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LinkButton>Question about this transaction?</LinkButton>
          </div>
        </div>
        <Divider />
      </AccordionItemPanel>
    </AccordionItem>
  )
}

export default ({ history }) => {
  const { id } = useParams()
  const account = useSelector(state =>
    state.accounts.accounts.find(c => c.id === id)
  )
  if (!account) {
    return <Redirect to="/accounts" />
  }

  const metadata = account.metadata || []

  const [searchText, setSearchText] = useState('')
  const dispatch = useDispatch()

  const allTransactions =
    searchText.length > 0
      ? account.transactions.filter(
          t => t.label.toLowerCase().search(searchText.toLowerCase()) !== -1
        )
      : account.transactions
  const pendingTransactions = allTransactions.filter(t => t.pending === true)
  const transactions = allTransactions.filter(t => t.pending !== true)

  return (
    <StandardLayout>
      <Heading1>
        <IconButton onClick={() => history.goBack()} style={{ marginRight: 8 }}>
          <i className="icon-chevron-left" />
        </IconButton>{' '}
        Transaction History
      </Heading1>

      <div style={{ maxWidth: 768 }}>
        <div style={{ marginBottom: '2rem' }}>
          <TextField
            type="search"
            name="transaction search"
            placeholder="Search transactions"
            value={searchText}
            onChange={evt => setSearchText(evt.target.value)}
            style={{ backgroundColor: 'white' }}
          />
        </div>

        <Card elevation={2}>
          <Card.Header style={{ padding: 0 }}>
            <ListItem>
              <Flex justify="space-between">
                <div>
                  <ListItem.LargeText>{account.nickname}</ListItem.LargeText>
                  <ListItem.SubText>
                    {account.mask ? '••••' + account.mask : null}
                  </ListItem.SubText>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ textAlign: 'right' }}>
                    <ListItem.LargeText>
                      {numeral(account.balance).format('$0,0.00')}
                    </ListItem.LargeText>
                  </div>
                </div>
              </Flex>
            </ListItem>
          </Card.Header>
          <Card.Body>
            <Row>
              {metadata.map((m, i) => (
                <Col xs={12} md={6} lg={4} key={`metadata-${i}`}>
                  <LabelValue
                    label={m.label}
                    value={m.format ? m.format(m.value) : m.value}
                    action={m.action}
                    style={{
                      marginRight: 40,
                      marginBottom: 16,
                      minWidth: '25%'
                    }}
                  />
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        <Card elevation={2}>
          <Card.Header>Pending Transactions</Card.Header>
          <TransactionAccordion allowZeroExpanded={true}>
            {pendingTransactions.map((t, i) => (
              <TransactionListItem
                key={i}
                {...t}
                style={{ paddingTop: 16, paddingBottom: 16 }}
                onNoteChange={note =>
                  dispatch({
                    type: 'UPDATE_TRANSACTION',
                    payload: {
                      account: id,
                      transaction: t.id,
                      data: {
                        note
                      }
                    }
                  })
                }
              />
            ))}
          </TransactionAccordion>
          {pendingTransactions.length === 0 && (
            <Card.Body>
              <Text muted nomargin>
                No transactions found.
              </Text>
            </Card.Body>
          )}
        </Card>

        <Card elevation={2}>
          <Card.Header>Transactions</Card.Header>

          <TransactionAccordion allowZeroExpanded={true}>
            {(transactions || []).map((t, i) => (
              <TransactionListItem
                key={i}
                {...t}
                style={{ paddingTop: 16, paddingBottom: 16 }}
                onNoteChange={note =>
                  dispatch({
                    type: 'UPDATE_TRANSACTION',
                    payload: {
                      account: id,
                      transaction: t.id,
                      data: {
                        note
                      }
                    }
                  })
                }
              />
            ))}
          </TransactionAccordion>
          {transactions.length === 0 && (
            <Card.Body>
              <Text muted nomargin>
                No transactions found.
              </Text>
            </Card.Body>
          )}
        </Card>
      </div>
    </StandardLayout>
  )
}
